<template>
  <div class="page-shell">
    <PageBanner
      eyebrow="HONORS"
      background-image="/page/honors-banner.jpg"
      :title="{ 'zh-CN': '荣誉资质', 'en-US': 'Honors & Qualifications' }"
      :subtitle="{ 'zh-CN': '资质证书、检验报告与试验合格证明展示', 'en-US': 'Qualification certificates, inspection reports and test certificates.' }"
    />

    <section class="section honors-section">
      <div class="container">
        <div class="honors-heading">
          <span>QUALIFICATIONS</span>
          <p>
            {{ $pick({
              "zh-CN": "以规范的检测资料与备案文件呈现产品质量基础，为客户选型、采购与项目验收提供可靠参考。",
              "en-US": "Standardized testing and filing documents present the product quality foundation and provide reliable references for selection, procurement and acceptance."
            }) }}
          </p>
        </div>

        <div class="honor-grid">
          <article
            v-for="item in honorItems"
            :key="item.image"
            v-reveal
            class="honor-card"
            role="button"
            tabindex="0"
            @click="openPreview(item)"
            @keydown.enter.prevent="openPreview(item)"
            @keydown.space.prevent="openPreview(item)"
          >
            <figure>
              <img :src="item.image" :alt="$pick(item.title)" loading="lazy" />
            </figure>
            <h3>{{ $pick(item.title) }}</h3>
          </article>
        </div>
      </div>
    </section>

    <div v-if="selectedHonor" class="preview-mask" @click="closePreview">
      <div class="preview-panel" role="dialog" aria-modal="true" @click.stop>
        <button type="button" class="preview-close" :aria-label="$pick(closeText)" @click="closePreview">×</button>
        <img :src="selectedHonor.image" :alt="$pick(selectedHonor.title)" />
        <h3>{{ $pick(selectedHonor.title) }}</h3>
      </div>
    </div>
  </div>
</template>

<script>
import PageBanner from "../../components/common/PageBanner.vue";

const honorItems = [
  {
    title: { "zh-CN": "S20-M-2000/10-NX2检验报告", "en-US": "S20-M-2000/10-NX2 Inspection Report" },
    image: "/honors/s20-m-2000-inspection-report.png"
  },
  {
    title: { "zh-CN": "S20-M-2000/10-NX2试验合格证书", "en-US": "S20-M-2000/10-NX2 Test Certificate" },
    image: "/honors/s20-m-2000-test-certificate.png"
  },
  {
    title: { "zh-CN": "S20-M-630~2500/10-NX2型号备案证书", "en-US": "S20-M-630~2500/10-NX2 Model Filing Certificate" },
    image: "/honors/s20-model-filing-certificate.png"
  },
  {
    title: { "zh-CN": "SCB14-2000/10-NX2检验报告", "en-US": "SCB14-2000/10-NX2 Inspection Report" },
    image: "/honors/scb14-2000-inspection-report.png"
  },
  {
    title: { "zh-CN": "SCB14-2000/10-NX2试验合格证书", "en-US": "SCB14-2000/10-NX2 Test Certificate" },
    image: "/honors/scb14-2000-test-certificate.png"
  },
  {
    title: { "zh-CN": "SCB14-630~2500/10-NX2型号备案证书", "en-US": "SCB14-630~2500/10-NX2 Model Filing Certificate" },
    image: "/honors/scb14-model-filing-certificate.png"
  }
];

export default {
  name: "HonorsPage",
  components: { PageBanner },
  data() {
    return {
      honorItems,
      selectedHonor: null,
      closeText: { "zh-CN": "关闭", "en-US": "Close" }
    };
  },
  mounted() {
    window.addEventListener("keydown", this.handleKeydown);
  },
  beforeDestroy() {
    window.removeEventListener("keydown", this.handleKeydown);
    document.body.classList.remove("honor-preview-open");
  },
  methods: {
    openPreview(item) {
      this.selectedHonor = item;
      document.body.classList.add("honor-preview-open");
    },
    closePreview() {
      this.selectedHonor = null;
      document.body.classList.remove("honor-preview-open");
    },
    handleKeydown(event) {
      if (event.key === "Escape" && this.selectedHonor) {
        this.closePreview();
      }
    }
  }
};
</script>

<style scoped>
:global(body.honor-preview-open) {
  overflow: hidden;
}

.honors-section {
  background:
    linear-gradient(180deg, rgba(243, 248, 255, 0.88), rgba(255, 255, 255, 0) 260px),
    #fff;
}

.honors-heading {
  position: relative;
  margin-bottom: 30px;
  padding-top: 16px;
  border-top: 2px solid #1c78df;
}

.honors-heading::before {
  content: "";
  position: absolute;
  top: -2px;
  left: 0;
  width: 270px;
  height: 11px;
  background: linear-gradient(90deg, #0b66d8, #31a9ff);
  clip-path: polygon(0 0, 92% 0, 100% 100%, 0 100%);
}

.honors-heading span {
  display: inline-block;
  margin-top: 18px;
  color: var(--brand-blue-soft);
  font-size: 13px;
  font-weight: 800;
  letter-spacing: 0.16em;
}

.honors-heading p {
  max-width: 780px;
  margin: 12px 0 0;
  color: #657284;
  line-height: 1.8;
}

.honor-grid {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 28px 22px;
}

.honor-card {
  position: relative;
  display: flex;
  min-width: 0;
  flex-direction: column;
  background: #fff;
  border: 1px solid #dfe5ef;
  box-shadow: 0 12px 30px rgba(24, 73, 132, 0.09);
  cursor: zoom-in;
  outline: none;
  transition: transform 0.28s ease, box-shadow 0.28s ease, border-color 0.28s ease;
}

.honor-card figure {
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 360px;
  margin: 0;
  padding: 16px;
  background:
    linear-gradient(135deg, rgba(244, 248, 253, 0.96), rgba(255, 255, 255, 0.98)),
    #f6f9fd;
  border-bottom: 1px solid #e7edf6;
}

.honor-card img {
  width: 100%;
  height: 330px;
  object-fit: contain;
  filter: drop-shadow(0 10px 20px rgba(22, 64, 116, 0.1));
  transition: transform 0.32s ease;
}

.honor-card h3 {
  margin: 0;
  padding: 18px 18px 20px;
  color: #2f4056;
  font-size: 18px;
  font-weight: 600;
  line-height: 1.45;
  text-align: center;
}

.honor-card:hover {
  transform: translateY(-5px);
  border-color: rgba(16, 112, 214, 0.38);
  box-shadow: 0 24px 48px rgba(24, 73, 132, 0.16);
}

.honor-card:focus-visible {
  border-color: rgba(16, 112, 214, 0.72);
  box-shadow: 0 0 0 4px rgba(28, 120, 223, 0.16), 0 24px 48px rgba(24, 73, 132, 0.16);
}

.honor-card:hover img {
  transform: scale(1.025);
}

.preview-mask {
  position: fixed;
  z-index: 3000;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 32px;
  background: rgba(4, 15, 32, 0.78);
  backdrop-filter: blur(8px);
}

.preview-panel {
  position: relative;
  width: min(100%, 980px);
  max-height: calc(100vh - 64px);
  padding: 24px 24px 20px;
  background: #fff;
  border: 1px solid rgba(210, 224, 242, 0.9);
  box-shadow: 0 34px 90px rgba(0, 20, 48, 0.36);
  overflow: auto;
}

.preview-panel img {
  width: 100%;
  max-height: calc(100vh - 170px);
  object-fit: contain;
  background: #f5f8fc;
}

.preview-panel h3 {
  margin: 16px 48px 0 0;
  color: var(--brand-blue-deep);
  font-size: 20px;
  line-height: 1.45;
}

.preview-close {
  position: absolute;
  top: 14px;
  right: 14px;
  width: 42px;
  height: 42px;
  color: var(--brand-blue-deep);
  background: #fff;
  border: 1px solid #d8e4f2;
  border-radius: 50%;
  font-size: 30px;
  line-height: 1;
  cursor: pointer;
  box-shadow: 0 10px 24px rgba(16, 62, 118, 0.14);
}

@media (max-width: 1100px) {
  .honor-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}

@media (max-width: 640px) {
  .honor-grid {
    grid-template-columns: 1fr;
    gap: 22px;
  }

  .honors-heading::before {
    width: 190px;
  }

  .honor-card figure {
    min-height: 300px;
    padding: 12px;
  }

  .honor-card img {
    height: 280px;
  }

  .preview-mask {
    padding: 14px;
  }

  .preview-panel {
    max-height: calc(100vh - 28px);
    padding: 14px;
  }

  .preview-panel img {
    max-height: calc(100vh - 126px);
  }

  .preview-panel h3 {
    margin-right: 52px;
    font-size: 17px;
  }
}
</style>
